import { useContext } from 'react';

import { TrackingContext } from '@client/components/TrackingProvider';
import { datadogLogs } from '@datadog/browser-logs';

export type TTrackPageViewFunc = (
    trackData: { pageType: string; customProperties?: Record<string, unknown> }
) => void;
type TTrackEventParams = {
    id: string;
    url?: string;
    contentId?: string;
    objectId?: string;
    elementType?: string;
    [x: string]: unknown;
};
type TTrackEventWithParamsFunc = (params: TTrackEventParams, eventType?: string) => void;
type TTrackAndRedirectToExternalUrlFunc = (trackingAttributeId: string, url: string) => void;

export type TTrackingProps = {
    trackPageView: TTrackPageViewFunc;
    trackEventWithParams: TTrackEventWithParamsFunc;
    trackAndRedirectToExternalUrl: TTrackAndRedirectToExternalUrlFunc;
};

const EXTERNAL_REDIRECT_TIMEOUT = 500;

export const useTracking = (): TTrackingProps => {
    const { trackEvent, trackPageView } = useContext(TrackingContext);

    if (!trackEvent || !trackPageView) {
        throw new Error('trackEvent or trackPageView is not available in TrackingContext');
    }

    const trackEventWithParams: TTrackEventWithParamsFunc = (params, eventType = 'uiEvent') => {
        trackEvent({ eventType, params }).catch((error: unknown) => {
            if (error instanceof Error) {
                datadogLogs.logger.error('Error when tracking event:', { params }, error);
            } else {
                datadogLogs.logger.error('Error when tracking event:', { params });
            }
        });
    };

    const trackAndRedirectToExternalUrl = (trackingAttributeId: string, url: string = window.location.href) => {
        if (trackingAttributeId) {
            trackEventWithParams({ id: trackingAttributeId, url });
        }
        if (url) {
            setTimeout(() => window.location.assign(url), EXTERNAL_REDIRECT_TIMEOUT);
        }
    };

    return { trackPageView, trackEventWithParams, trackAndRedirectToExternalUrl };
};
